import { Alert } from '@/components/ui/alert';
import { Box, Button, Skeleton, Text, VStack } from '@chakra-ui/react';
import {
  type DisplayRightsActionBlocker,
  type MentionBoostAuthorizationScope,
} from '@fluenceur/types';
import { WarningTriangle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { DisplayRightsCopyLinkButton } from './DisplayRightsCopyLinkButton';
import { DisplayRightsComposerForm } from './DisplayRightsComposerForm';
import { DisplayRightsReplaceNotice } from './DisplayRightsReplaceNotice';
import { DisplayRightsSentPanel } from './DisplayRightsSentPanel';
import { DisplayRightsStatusBadges } from './DisplayRightsStatusBadges';
import type { DisplayRightsMetaState } from './display-rights-status';
import { useDisplayRightsComposer } from './use-display-rights-composer';

/**
 * Why nothing can be composed, in the brand's words. One sentence per cause,
 * never a shared one: "an agreement is running" is a come-back-later, "the next
 * term is already agreed" is a launch-it, and a withdrawn authorization is a
 * dead end. Collapsing them was how the composer ended up telling a brand to
 * wait for something it already had.
 */
const UNAVAILABLE_REASON_KEYS: Record<DisplayRightsActionBlocker, string> = {
  agreement_running: 'displayRights.composer.unavailable',
  agreement_pending: 'displayRights.composer.agreementPending',
  authorization_closed: 'displayRights.composer.authorizationClosed',
};

interface DisplayRightsComposerProps {
  authorizationId: string;
  /** Personalises the pre-filled message when the surface knows the handle. */
  creatorHandle?: string | null;
  /** Meta side of the crossed state, shown once the proposal is out. */
  metaState?: DisplayRightsMetaState;
  /**
   * What the authorization covers. Shown above the fields: a brand about to
   * commit money must see whether it is paying for one media or for the whole
   * account. Omitted when the surface cannot tell.
   */
  scope?: MentionBoostAuthorizationScope;
  /**
   * Opening amount and run time for a new chain, when the caller brings its own
   * (the companion's renewal card). Ignored on a revision.
   */
  initialTerms?: { amountCents: number; durationMonths: number } | null;
  /** Secondary way out: "skip" in the request flow, "back" in the contact card. */
  secondaryAction?: { label: string; onClick: () => void };
  /** Called from the success panel. Omit to leave the outcome on screen. */
  onDone?: () => void;
}

/**
 * The way out of a screen that offers no primary action.
 *
 * Outline, not ghost. A ghost button reads fine directly under a filled CTA —
 * that is the composer form, where "skip" is deliberately quieter than "send".
 * On a terminal screen there is nothing to be quieter than, so ghost is simply
 * an invisible control on a dead end: the one place the brand most needs a
 * visible exit. Same treatment on all of them (closed authorization, failed
 * context load) so the exit never moves.
 */
function DisplayRightsTerminalFooter({
  action,
}: {
  action?: { label: string; onClick: () => void };
}) {
  if (!action) {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      width="full"
      onClick={action.onClick}
      data-testid="display-rights-terminal-exit"
    >
      {action.label}
    </Button>
  );
}

/**
 * The one composer, shared by both entry points: the authorization request flow
 * and the contact card. Whatever opened it, the gesture (first proposal,
 * replacement, renewal) is resolved by the server, not by the caller.
 */
export function DisplayRightsComposer({
  authorizationId,
  creatorHandle,
  metaState,
  scope,
  initialTerms,
  secondaryAction,
  onDone,
}: DisplayRightsComposerProps) {
  const { t } = useTranslation();
  const composer = useDisplayRightsComposer({ authorizationId, creatorHandle, initialTerms });

  if (composer.result) {
    return (
      <DisplayRightsSentPanel result={composer.result} metaState={metaState} onDone={onDone} />
    );
  }

  if (composer.isLoading) {
    return (
      <VStack align="stretch" gap="3" data-testid="display-rights-composer-loading">
        <Skeleton h="60px" rounded="md" />
        <Skeleton h="96px" rounded="md" />
        <Skeleton h="40px" rounded="md" />
      </VStack>
    );
  }

  if (composer.isError || !composer.context) {
    return (
      <VStack align="stretch" gap="3" data-testid="display-rights-composer-error">
        <Alert
          status="error"
          variant="surface"
          title={t('displayRights.composer.errors.contextTitle')}
          icon={<WarningTriangle width={16} height={16} />}
        >
          {t('displayRights.composer.errors.contextDescription')}
        </Alert>
        <Button variant="outline" size="sm" width="full" onClick={() => void composer.refetch()}>
          {t('common.retry')}
        </Button>
        <DisplayRightsTerminalFooter action={secondaryAction} />
      </VStack>
    );
  }

  // An agreement is running, or the authorization can no longer carry terms.
  // Showing a disabled form would suggest a move that does not exist — and the
  // two reasons are not interchangeable, so neither is the copy: one is a
  // "come back later", the other is a dead end the brand must understand.
  if (composer.context.action === 'none') {
    // Three causes, three sentences. `agreement_pending` is the one that must
    // not be folded into "come back later": the brand already holds the next
    // term and the way out is to launch, not to wait.
    const blocker = composer.context.actionBlocker ?? 'authorization_closed';
    const reasonKey = UNAVAILABLE_REASON_KEYS[blocker];

    return (
      <VStack align="stretch" gap="3" data-testid="display-rights-composer-unavailable">
        <DisplayRightsStatusBadges proposal={composer.context.current} metaState={metaState} />
        <Box>
          <Text
            fontSize="sm"
            color="fg.muted"
            lineHeight="tall"
            data-testid="display-rights-unavailable-reason"
            data-reason={blocker}
          >
            {t(reasonKey)}
          </Text>
        </Box>
        {/* The link of a still-open version stays reachable even here. */}
        <DisplayRightsCopyLinkButton
          proposal={composer.context.current}
          size="sm"
          alignSelf="flex-start"
        />
        <DisplayRightsTerminalFooter action={secondaryAction} />
      </VStack>
    );
  }

  if (composer.needsReplacementConfirmation) {
    return (
      <DisplayRightsReplaceNotice
        onConfirm={composer.confirmReplacement}
        onCancel={secondaryAction?.onClick ?? (() => undefined)}
      />
    );
  }

  return (
    <DisplayRightsComposerForm
      form={composer.form}
      context={composer.context}
      scope={scope}
      isSubmitting={composer.isSubmitting}
      onSubmit={() => void composer.submit()}
      secondaryAction={secondaryAction}
    />
  );
}
